import { useNavigate } from 'react-router-dom';
import ActionButton from '../../components/ui/ActionButton';
import DataTable, { Column } from '../../components/ui/DataTable';
import {
  UploadStatus,
  PIPELINE_STEPS,
  getStepIndex,
  StatRow,
} from './types';

interface PipelineProgressProps {
  activeUpload: UploadStatus;
  showRejected: boolean;
  setShowRejected: (v: boolean) => void;
  setActiveUpload: (v: UploadStatus | null) => void;
}

export default function PipelineProgress({
  activeUpload,
  showRejected,
  setShowRejected,
  setActiveUpload,
}: PipelineProgressProps) {
  const navigate = useNavigate();
  const { status, status_detail } = activeUpload;
  const currentIdx = getStepIndex(status);
  const isComplete = status === 'COMPLETE';
  const isFailed = status === 'FAILED';

  const rejectedColumns: Column[] = [
    { key: 'row_number', label: 'Row', render: (v: any) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>{v}</span> },
    { key: 'code', label: 'Code', render: (v: any) => (
      <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: '#fca5a5' }}>
        {v}
      </span>
    )},
    { key: 'reason', label: 'Reason' },
  ];

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-default)',
      borderRadius: 8, padding: 20, marginBottom: 20,
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: 'var(--text-primary)' }}>
        Processing: {activeUpload.filename}
      </div>

      {/* Steps */}
      {PIPELINE_STEPS.map((step, i) => {
        let icon = '  ';  // pending
        let color = 'var(--text-tertiary)';

        if (isComplete || i < currentIdx) {
          icon = '[OK]';
          color = 'var(--positive)';
        } else if (isFailed && i === currentIdx) {
          icon = '[X]';
          color = '#ef4444';
        } else if (i === currentIdx) {
          icon = '>>';
          color = 'var(--navy-700)';
        }

        return (
          <div key={step.key} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
            fontSize: 12, color,
          }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, width: 36 }}>{icon}</span>
            <span style={{ fontWeight: i === currentIdx && !isComplete ? 700 : 400 }}>{step.label}</span>
            {i === currentIdx && !isComplete && !isFailed && (
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 8 }}>
                {status_detail ? status_detail : `(Step ${i + 1}/${PIPELINE_STEPS.length})`}
              </span>
            )}
          </div>
        );
      })}

      {/* Terminal states */}
      {isComplete && (
        <div style={{ marginTop: 16, padding: 16, background: 'rgba(34,197,94,0.08)', borderRadius: 6, border: '1px solid rgba(34,197,94,0.2)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--positive)', marginBottom: 10 }}>Upload Complete</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 12 }}>
            <StatRow label="Rows Ingested" value={activeUpload.rows_ingested} />
            <StatRow label="Rows Rejected" value={activeUpload.rows_rejected} />
            <StatRow label="Exceptions Found" value={activeUpload.exceptions_detected} />
            <StatRow label="JEs Created" value={activeUpload.jes_created} />
            <StatRow label="Actions Created" value={activeUpload.actions_created} />
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <ActionButton label="View Exceptions" onClick={() => navigate('/tasks')} variant="secondary" size="sm" />
            {(activeUpload.rows_rejected || 0) > 0 && (
              <ActionButton label={`View Rejected Rows (${activeUpload.rows_rejected})`} onClick={() => setShowRejected(!showRejected)} variant="secondary" size="sm" />
            )}
            <ActionButton label="Go to Dashboard" onClick={() => navigate('/')} variant="secondary" size="sm" />
          </div>
        </div>
      )}

      {isFailed && (
        <div style={{ marginTop: 16, padding: 16, background: 'rgba(239,68,68,0.08)', borderRadius: 6, border: '1px solid rgba(239,68,68,0.2)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#ef4444', marginBottom: 6 }}>Upload Failed</div>
          <div style={{ fontSize: 12, color: '#fca5a5' }}>{status_detail || 'Unknown error'}</div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            {(activeUpload.rows_rejected || 0) > 0 && (
              <ActionButton label="View Rejected Rows" onClick={() => setShowRejected(!showRejected)} variant="secondary" size="sm" />
            )}
            <ActionButton label="Fix and Re-upload" onClick={() => setActiveUpload(null)} variant="secondary" size="sm" />
          </div>
        </div>
      )}

      {/* Rejected rows */}
      {showRejected && activeUpload.rejected_rows.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            Rejected Rows (first {activeUpload.rejected_rows.length})
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <DataTable
              columns={rejectedColumns}
              rows={activeUpload.rejected_rows}
              emptyMessage="No rejected rows."
            />
          </div>
        </div>
      )}
    </div>
  );
}
